import { useEffect, useState } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import type { CronMonitorListKind, CronMonitorTimeRange } from '@/features/jobs/types'
import type { CronMonitorFiltersProps, CronMonitorListFilters } from '../types'

const LABEL_CLASS = 'text-xs font-semibold uppercase tracking-wide text-slate-500'
const INPUT_CLASS =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]/15'

export const DEFAULT_CRON_MONITOR_LIST_FILTERS: CronMonitorListFilters = {
  job_name: '',
  queue: '',
  time_range: '24h',
}

export function countActiveCronMonitorFilters(
  filters: CronMonitorListFilters,
  kind: CronMonitorListKind,
): number {
  let count = 0
  if (kind !== 'missed' && filters.queue) count += 1
  if ((kind === 'failed' || kind === 'successful') && filters.time_range !== '24h') count += 1
  return count
}

export function CronMonitorFilters({
  kind,
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: CronMonitorFiltersProps) {
  const [draft, setDraft] = useState<CronMonitorListFilters>(filters)

  useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  const showQueueType = kind !== 'missed'
  const showTimeRange = kind === 'failed' || kind === 'successful'

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={() => {
        const cleared: CronMonitorListFilters = {
          ...DEFAULT_CRON_MONITOR_LIST_FILTERS,
          time_range: kind === 'failed' || kind === 'successful' ? '24h' : filters.time_range,
        }
        setDraft(cleared)
        onReset()
      }}
      onDone={() => {
        onApply(draft)
      }}
      testIdPrefix="cron-monitor"
    >
      {showQueueType ? (
        <div className="space-y-2">
          <label className={LABEL_CLASS} htmlFor="cron-monitor-filter-type">
            Type
          </label>
          <select
            id="cron-monitor-filter-type"
            value={draft.queue}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                queue: event.target.value,
              }))
            }
            className={INPUT_CLASS}
          >
            <option value="">All types</option>
            <option value="default">Default</option>
            <option value="onDemand">On demand</option>
            <option value="otp">OTP</option>
          </select>
        </div>
      ) : null}

      {showTimeRange ? (
        <div className="space-y-2">
          <label className={LABEL_CLASS} htmlFor="cron-monitor-filter-time-range">
            Time range
          </label>
          <select
            id="cron-monitor-filter-time-range"
            value={draft.time_range}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                time_range: event.target.value as CronMonitorTimeRange,
              }))
            }
            className={INPUT_CLASS}
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      ) : null}
    </ListFilterDrawer>
  )
}
